import type { QualityLevel } from '../engine/quality';
import type { CameraMode } from '../engine/CameraRig';
import { DEFAULT_VEHICLE_ID, VEHICLES_BY_ID } from '../vehicles/vehicleData';

export type ControlMode = 'buttons' | 'wheel' | 'gyro';
export type GameMode = 'free' | 'parking';

export interface Settings {
  controlMode: ControlMode;
  gameMode: GameMode;
  quality: QualityLevel;
  qualityAuto: boolean;
  vehicleId: string;
  paintColor: number;
  cameraMode: CameraMode;
  vibration: boolean;
  sound: boolean;
  showPerf: boolean;
  gyroSensitivity: number;
}

const KEY = 'motorwelt.settings.v1';

export const DEFAULT_SETTINGS: Settings = {
  controlMode: 'buttons',
  gameMode: 'free',
  quality: 'medium',
  qualityAuto: true,
  vehicleId: 'stadtflitzer',
  paintColor: 0xe23b2e,
  cameraMode: 'chase',
  vibration: true,
  sound: true,
  showPerf: false,
  gyroSensitivity: 1,
};

/**
 * Einstellungen liegen in localStorage: winzig, synchron lesbar und damit
 * schon vor dem ersten Frame verfuegbar. Der eigentliche Spielstand
 * (Muenzen, Sterne, freigeschaltete Fahrzeuge) kommt in IndexedDB - siehe M7.
 */
export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return sanitizeSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Privater Modus o. ae. - Spielen geht trotzdem weiter.
  }
}

function sanitizeSettings(value: unknown): Settings {
  if (!isRecord(value)) return { ...DEFAULT_SETTINGS };

  return {
    controlMode: isControlMode(value['controlMode']) ? value['controlMode'] : DEFAULT_SETTINGS.controlMode,
    gameMode: isGameMode(value['gameMode']) ? value['gameMode'] : DEFAULT_SETTINGS.gameMode,
    quality: isQualityLevel(value['quality']) ? value['quality'] : DEFAULT_SETTINGS.quality,
    qualityAuto: booleanOr(value['qualityAuto'], DEFAULT_SETTINGS.qualityAuto),
    vehicleId: isVehicleId(value['vehicleId']) ? value['vehicleId'] : DEFAULT_VEHICLE_ID,
    paintColor: isPaintColor(value['paintColor']) ? value['paintColor'] : DEFAULT_SETTINGS.paintColor,
    cameraMode: isCameraMode(value['cameraMode']) ? value['cameraMode'] : DEFAULT_SETTINGS.cameraMode,
    vibration: booleanOr(value['vibration'], DEFAULT_SETTINGS.vibration),
    sound: booleanOr(value['sound'], DEFAULT_SETTINGS.sound),
    showPerf: booleanOr(value['showPerf'], DEFAULT_SETTINGS.showPerf),
    gyroSensitivity: isGyroSensitivity(value['gyroSensitivity'])
      ? value['gyroSensitivity']
      : DEFAULT_SETTINGS.gyroSensitivity,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isControlMode(value: unknown): value is ControlMode {
  return value === 'buttons' || value === 'wheel' || value === 'gyro';
}

function isGameMode(value: unknown): value is GameMode {
  return value === 'free' || value === 'parking';
}

function isQualityLevel(value: unknown): value is QualityLevel {
  return value === 'low' || value === 'medium' || value === 'high';
}

function isCameraMode(value: unknown): value is CameraMode {
  return value === 'chase' || value === 'hood' || value === 'cockpit';
}

function isVehicleId(value: unknown): value is string {
  return typeof value === 'string' && VEHICLES_BY_ID.has(value);
}

function isPaintColor(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0xffffff;
}

function isGyroSensitivity(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0.25 && value <= 3;
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
